import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { API_TYPES } from '@/main/app.constants';
import {
  appendOrReplaceBreadcrumb,
  clear,
  clearSelection,
  fetchHierarchyLayer,
  fetchScalingTableSearchItems,
  resetScalingTableSearchParams,
  resetTreeBuilderSearchParams,
  searchItems,
  setCurrentNodePath,
  setSelectedHierarchy,
  setUpHierarchiesSearchForItemTable,
} from '@/search/search.actions';
import { SEARCH_BREADCRUMB } from '@/search/search.constants';
import { LIMIT_IN_OVERVIEW } from '@/search/search.utilities';
import type { SearchModes, SearchPanes } from '@/search/search.constants';
import type { SelectedSearchItem } from '@/search/search.types';

/**
 * Explores a hierarchy level based on the breadcrumb item and search parameters. Used ofr traversing breadcrumbs in
 * the Data Pane
 * @param pane
 * @param params
 */
export const exploreHierarchyLevel = async (
  pane: SearchPanes,
  params: {
    item: SelectedSearchItem;
    scopeIds: string[];
    searchTypes: string[];
    workbooksFilter: SelectedSearchItem[];
    searchLocallyScoped: boolean;
    mode: SearchModes;
    filterByWorkbook?: boolean;
    excludeGloballyScoped: boolean;
  },
) => {
  const {
    item,
    scopeIds,
    searchTypes,
    workbooksFilter,
    searchLocallyScoped,
    mode,
    filterByWorkbook,
    excludeGloballyScoped,
  } = params;
  clearSelection();

  if (item.id === '') {
    clear(pane, searchTypes);
  } else if (item.id === SEARCH_BREADCRUMB.id) {
    // re-search with current filters to get back to original search state
    const scopedTo =
      workbooksFilter.length > 0 ? (scopeIds ?? []).concat(workbooksFilter.map(({ id }) => id)) : scopeIds;
    await searchItems(pane, searchTypes, scopedTo, excludeGloballyScoped || filterByWorkbook || searchLocallyScoped);
    resetTreeBuilderSearchParams(pane);
    appendOrReplaceBreadcrumb(pane, SEARCH_BREADCRUMB);
  } else if (item.type === SeeqNames.Types.TableDefinition) {
    setSelectedHierarchy(pane, undefined);
    setCurrentNodePath(pane, []);
    await setUpHierarchiesSearchForItemTable(pane, item, mode === 'search');
  } else if (item.type === SeeqNames.Types.Hierarchy) {
    await fetchHierarchyLayer(pane, item, item.id, [], mode === 'search');
    setCurrentNodePath(pane, []);
  } else if (item.ancestors && item.ancestors.some((ancestor) => ancestor.type === SeeqNames.Types.Hierarchy)) {
    const ancestors = item.ancestors ?? [];
    const hierarchy = ancestors.find((ancestor) => ancestor.type === SeeqNames.Types.Hierarchy)!;
    const currentNodePath = ancestors.filter((ancestor) => ancestor.node).map((ancestor) => ancestor.node!);
    await fetchHierarchyLayer(pane, item, hierarchy.id, currentNodePath, mode === 'search');
  }
};

/**
 * Explores a scaling table level based on the breadcrumb item and search parameters. Used for traversing breadcrumbs in
 * the Data Pane
 * @param pane
 * @param params
 */
export const exploreScalingTableLevel = async (
  pane: SearchPanes,
  params: {
    item: SelectedSearchItem;
    scopeIds: string[];
    searchTypes: string[];
    workbooksFilter: SelectedSearchItem[];
    searchLocallyScoped: boolean;
    mode: SearchModes;
    filterByWorkbook?: boolean;
    excludeGloballyScoped: boolean;
    setScalingTableIsCsv: (isCsv: boolean) => void;
  },
) => {
  const {
    item,
    scopeIds,
    searchTypes,
    workbooksFilter,
    searchLocallyScoped,
    mode,
    filterByWorkbook,
    excludeGloballyScoped,
    setScalingTableIsCsv,
  } = params;

  clearSelection();

  if (item.id === '') {
    setScalingTableIsCsv(false);
    clear(pane, searchTypes);
  } else if (item.id === SEARCH_BREADCRUMB.id) {
    // re-search with current filters to get back to original search state
    const scopedTo =
      workbooksFilter.length > 0 ? (scopeIds ?? []).concat(workbooksFilter.map(({ id }) => id)) : scopeIds;
    await searchItems(pane, searchTypes, scopedTo, excludeGloballyScoped || filterByWorkbook || searchLocallyScoped);
    resetScalingTableSearchParams(pane);
    appendOrReplaceBreadcrumb(pane, SEARCH_BREADCRUMB);
  } else {
    await fetchScalingTableSearchItems(pane, item, mode === 'search');
  }
};

export const filterByAssetItemType = (items: SelectedSearchItem[]) =>
  items.filter((item) => item.type === API_TYPES.ASSET);

const excludesItemType = (item: SelectedSearchItem, types: string[]) => !types.includes(item.type ?? '');

const filterByRestriction = (item: SelectedSearchItem, restrictExploration: boolean, searchTypes: string[]) =>
  restrictExploration ? searchTypes.includes(item.type ?? '') : true;

export const filterByName = (item: SelectedSearchItem, mode: SearchModes, nameFilter: string) =>
  ['overview', 'search', 'tree'].includes(mode) ? true : item?.name?.toLowerCase().includes(nameFilter.toLowerCase());

export const filterItems = (
  items: SelectedSearchItem[],
  mode: SearchModes,
  nameFilter: string,
  restrictExploration: boolean,
  searchTypes: string[],
) =>
  items
    .filter((i) => filterByName(i, mode, nameFilter))
    .filter((i) => filterByRestriction(i, restrictExploration, searchTypes))
    // CRAB-61422: This filter excludes FormulaItems as they are not rightly named in the UI and also not trendable
    .filter((i) => excludesItemType(i, [API_TYPES.ONNX]));

export const limitAndFilterItems = (
  items: SelectedSearchItem[],
  mode: SearchModes,
  nameFilter: string,
  restrictExploration: boolean,
  searchTypes: string[],
) =>
  filterItems(items, mode, nameFilter, restrictExploration, searchTypes).slice(
    0,
    mode === 'overview' ? LIMIT_IN_OVERVIEW : undefined,
  );

export const limitFilterAndSortItems = (
  items: SelectedSearchItem[],
  mode: SearchModes,
  nameFilter: string,
  restrictExploration: boolean,
  searchTypes: string[],
  sortFunc: (a: SelectedSearchItem, b: SelectedSearchItem) => number,
) => limitAndFilterItems(_.clone(items).sort(sortFunc), mode, nameFilter, restrictExploration, searchTypes);

export const sortByKeyValuesFirst = <T>(key: keyof T, values: any[]) => {
  return (a: T, b: T) => {
    let aMatch = false;
    let bMatch = false;
    values.forEach((value) => {
      if (a[key] === value) aMatch = true;
      if (b[key] === value) bMatch = true;
    });
    if (aMatch && !bMatch) return -1;
    if (!aMatch && bMatch) return 1;
    return 0;
  };
};
